import type { ChangeEvent, RefObject } from 'react';
import { useCallback, useRef, useState } from 'react';

import { parseIntegerAmount } from '#src/app/shared/utils/payments';

import { usePromoteFormState } from '../../../../../../../../formContext';
import { useTracking } from '../../../../../../../../shared/hooks/useTracking';

type Result = ModelHookResult<
	{
		customAmount: string;
		customInputRef: RefObject<HTMLInputElement | null>;
	},
	{
		handleCustomAmountSelect: () => void;
		handleCustomAmountFocus: () => void;
		handleCustomAmountChange: (value: ChangeEvent<HTMLInputElement>) => void;
	}
>;

export function useCustomAmount(): Result {
	const [{ validationErrors }, { setAmount, setValidationErrors }] = usePromoteFormState();

	const customInputRef = useRef<HTMLInputElement>(null);

	const [customAmount, setCustomAmount] = useState<number>(0);

	const { trackAmountInputFocus, trackAmountChanged, trackCustomAmountSelected } = useTracking();

	const handleCustomAmountFocus = useCallback((): void => {
		trackAmountInputFocus();
	}, [trackAmountInputFocus]);

	const handleCustomAmountChange = useCallback(
		(value: ChangeEvent<HTMLInputElement>): void => {
			const result = parseIntegerAmount(value.target.value);
			if (!result.isValid) {
				return;
			}
			setAmount(result.integerPart);
			setCustomAmount(result.integerPart);
			setValidationErrors({ ...validationErrors, amount: '', amountSelection: '', customAmount: '' });
			trackAmountChanged({ buttonWasClicked: false });
		},
		[setAmount, trackAmountChanged, setValidationErrors, validationErrors],
	);

	const handleCustomAmountSelect = useCallback((): void => {
		trackCustomAmountSelected();
		trackAmountChanged({ buttonWasClicked: false });
		setAmount(customAmount);
	}, [setAmount, customAmount, trackCustomAmountSelected, trackAmountChanged]);

	return {
		data: {
			customAmount: customAmount > 0 ? `${customAmount}` : '',
			customInputRef,
		},
		actions: {
			handleCustomAmountSelect,
			handleCustomAmountFocus,
			handleCustomAmountChange,
		},
	};
}
